import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { Tag } from "../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatWalletBalance } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type { WalletRow } from "./personDetailTypes";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const PERSON_WALLET_COLUMNS_COLUMN_WIDTH = {
  walletProductName: 200,
  balance: 160,
  validFrom: 140,
  validTo: 140,
  allowsAccess: 140,
  isActive: 120
} as const;

const WALLET_MIN_ACCESS_BALANCE = 0;

type TextFn = I18nContextValue["text"];

export const PERSON_WALLET_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "walletProductName", label: "Product", defaultVisible: true },
  { key: "balance", label: "Balance", defaultVisible: true },
  { key: "validFrom", label: "Valid from", defaultVisible: true },
  { key: "validTo", label: "Valid until", defaultVisible: true },
  { key: "allowsAccess", label: "Access right", defaultVisible: true },
  { key: "isActive", label: "Status", defaultVisible: true }
];

export function createPersonWalletColumns(text: TextFn, units: UnitsSettings): Record<string, ListColumn<WalletRow>> {
  return {
    walletProductName: {
      title: text("Product"),
      dataIndex: "walletProductName",
      key: "walletProductName",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.walletProductName
    },
    balance: {
      title: text("Balance"),
      dataIndex: "balance",
      key: "balance",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.balance,
      render: (_, row) => formatWalletBalance(Number(row.balance || 0), Number(row.walletType || 0), units)
    },
    validFrom: {
      title: text("Valid from"),
      dataIndex: "validFrom",
      key: "validFrom",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.validFrom,
      defaultSortOrder: "descend",
      render: (_, row) => formatDateOnly(String(row.validFrom || "").slice(0, ISO_DATE_LENGTH), units)
    },
    validTo: {
      title: text("Valid until"),
      dataIndex: "validTo",
      key: "validTo",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.validTo,
      render: (_, row) => (row.validTo ? formatDateOnly(String(row.validTo).slice(0, ISO_DATE_LENGTH), units) : "\u2014")
    },
    allowsAccess: {
      title: text("Access right"),
      dataIndex: "allowsAccess",
      key: "allowsAccess",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.allowsAccess,
      render: (_, row) => {
        const hasBalanceForAccess = Number(row.balance || 0) > WALLET_MIN_ACCESS_BALANCE;
        const effectiveAllowsAccess = row.allowsAccess === true && hasBalanceForAccess;
        return effectiveAllowsAccess ? <Tag color="green">{text("Yes")}</Tag> : <Tag color="red">{text("No")}</Tag>;
      }
    },
    isActive: {
      title: text("Status"),
      dataIndex: "isActive",
      key: "isActive",
      width: PERSON_WALLET_COLUMNS_COLUMN_WIDTH.isActive,
      render: (_, row) =>
        row.isActive === false ? <Tag color="red">{text("Locked")}</Tag> : <Tag color="green">{text("Active")}</Tag>
    }
  };
}
